import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { switchMap } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { Widgets } from '@common/enums';
import type { SelectedEntity } from '@common/types/layout.type';
import { ESelectedEntityType } from '@common/types/layout.type';

import type { WebLegacyWidgetEntity } from '@web/types';

import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { widgetNameOf } from '@common/helpers/widget-envelope';

@Component({
    selector: 'web-navigation-widget-item',
    templateUrl: './navigation-widget-item.component.html',
    styleUrls: ['./navigation-widget-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, TranslateModule, TooltipModule],
})
export class NavigationWidgetItemComponent {
    public readonly entity = input.required<WebLegacyWidgetEntity>();
    public readonly index = input.required<number>();
    public readonly selectedEntity = input.required<SelectedEntity>();
    public readonly isStructuredWidget = input(false);

    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);

    private readonly entity$ = toObservable(this.entity);

    public readonly extendedWidget = computed(() => this.widgetsService.getWidgetModel(widgetNameOf(this.entity())));

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);

    private readonly isCartWidget = computed(() => widgetNameOf(this.entity()) === Widgets.SHOP_CART);

    public readonly isDeleteDisabled = computed(() => this.isCartWidget());
    public readonly isCopyDisabled = computed(() => this.isCartWidget());

    public readonly isZeroWidget = computed(() => this.entity().type === 'zero');

    public readonly displayId = computed(() => this.entity().displayId || 'widget-' + this.entity().id);

    public readonly hideOnDesktop = computed(() => {
        const data = this.entity().data;
        return data?.hideOnDesktop ?? false;
    });

    public readonly hideOnMobile = computed(() => {
        const data = this.entity().data;
        return data?.hideOnMobile ?? false;
    });

    public readonly hideOnTablet = computed(() => {
        const data = this.entity().data;
        return data?.hideOnTablet ?? false;
    });

    public readonly productWidgetRestricted = toSignal(
        this.entity$.pipe(switchMap((entity) => this.productRestrictionFacade.widgetRestricted$(entity))),
        { initialValue: false }
    );

    public selectEntity(): void {
        if (this.isActive()) {
            return;
        }

        this.layoutFacade.setSelectedEntityWithScroll({
            entityId: this.entity().id,
            entityType: ESelectedEntityType.widget,
        });
    }

    public copyWidget(): void {
        if (this.isCopyDisabled() || this.productWidgetRestricted()) {
            return;
        }

        this.widgetsFacade.copyWidget(this.entity(), this.index());
    }

    public removeWidget(): void {
        if (this.isDeleteDisabled() || this.productWidgetRestricted()) {
            return;
        }

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.widgetsFacade.removeWidget(this.entity(), this.index());
    }

    public hideOnDesktopChange(value: boolean): void {
        this.widgetsFacade.updateWidgetData(this.entity().id, { ...this.entity().data, hideOnDesktop: value });
    }

    public hideOnTabletChange(value: boolean): void {
        this.widgetsFacade.updateWidgetData(this.entity().id, { ...this.entity().data, hideOnTablet: value });
    }

    public hideOnMobileChange(value: boolean): void {
        this.widgetsFacade.updateWidgetData(this.entity().id, { ...this.entity().data, hideOnMobile: value });
    }
}
